/** @format */

import React, { useState, useMemo } from "react";
import useCourseAuthors from "../hooks/useCourseAuthors";
import { useAuth } from "../contexts/AuthContext";
import {
  PencilIcon,
  EyeIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  StarIcon,
  CurrencyDollarIcon,
  UserIcon,
  ClockIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronUpDownIcon,
  UsersIcon,
  UserPlusIcon,
  Squares2X2Icon,
  ListBulletIcon,
  FireIcon,
  TrophyIcon,
  BookOpenIcon,
  SparklesIcon,
  HeartIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";
import { formatDate } from "../utils/dateUtils";
import { getCourseCategoryDisplayName } from "../utils/categoryUtils";
import CourseClassroomIndicator from "./CourseClassroomIndicator";

const CourseTable = ({
  courses,
  onView,
  onEdit,
  onDelete,
  onAssignAuthor,
  onAssignStudents,
  onToggleFeatured,
  loading,
}) => {
  const { currentUser, isAdmin, userRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [viewMode, setViewMode] = useState("cards"); // 'cards' or 'table'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12); // Default to 12 for cards, will adjust for table

  // Fetch course authors for displaying author information
  const { getAuthorId, getAuthorName, getAuthorNameFromCourse } =
    useCourseAuthors(courses);

  // Check if user can edit a specific course
  const canEditCourse = (course) => {
    if (isAdmin) return true;
    if (userRole === "author" && currentUser) {
      const isOwner =
        // Check by Firebase UID
        course.userId === currentUser.uid ||
        course.author === currentUser.uid ||
        // Check by email
        course.author === currentUser.email ||
        // Check by display name
        course.author === currentUser.displayName ||
        // Check by author_id field (if it exists)
        course.author_id === currentUser.uid ||
        // Check if author field contains current user info
        (typeof course.author === "string" &&
          course.author.includes(currentUser.email)) ||
        (typeof course.author === "string" &&
          course.author.includes(currentUser.displayName));
      return isOwner;
    }
    return false;
  };

  // Check if user can delete a specific course
  const canDeleteCourse = (course) => {
    return canEditCourse(course);
  };

  // Check if user can assign students to a specific course
  const canAssignStudents = (course) => {
    if (isAdmin) return true;
    if (userRole === "author" && currentUser) {
      const isOwner =
        // Check by Firebase UID
        course.userId === currentUser.uid ||
        course.author === currentUser.uid ||
        // Check by email
        course.author === currentUser.email ||
        // Check by display name
        course.author === currentUser.displayName ||
        // Check by author_id field (if it exists)
        course.author_id === currentUser.uid ||
        // Check if author field contains current user info
        (typeof course.author === "string" &&
          course.author.includes(currentUser.email)) ||
        (typeof course.author === "string" &&
          course.author.includes(currentUser.displayName));
      return isOwner;
    }
    return false;
  };

  // Check if user can assign authors to a specific course
  const canAssignAuthor = (course) => {
    return isAdmin; // Only admins can assign authors
  };

  // Adjust items per page based on view mode
  const effectiveItemsPerPage =
    viewMode === "cards" ? itemsPerPage : Math.max(itemsPerPage, 8);

  // Filter and sort courses
  const { filteredAndSortedCourses, paginatedCourses, totalPages, totalItems } =
    useMemo(() => {
      let filtered = courses.filter((course) => {
        const matchesSearch =
          course.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (typeof course.author === "string"
            ? course.author.toLowerCase().includes(searchTerm.toLowerCase())
            : false) ||
          getAuthorNameFromCourse(course)
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          course.meta?.summary
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

        const matchesStatus =
          statusFilter === "all" || course.status === statusFilter;
        const matchesPrice =
          priceFilter === "all" || course.price_status === priceFilter;
        const matchesFeatured =
          featuredFilter === "all" ||
          (featuredFilter === "featured" && course.featured === true) ||
          (featuredFilter === "not-featured" &&
            (course.featured === false || !course.featured));

        const matchesCategory =
          categoryFilter === "all" || course.cat_id === categoryFilter;

        return (
          matchesSearch &&
          matchesStatus &&
          matchesPrice &&
          matchesFeatured &&
          matchesCategory
        );
      });

      // Sort courses
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle nested properties
        if (sortField === "author_name") {
          aValue = a.author?.name || "";
          bValue = b.author?.name || "";
        } else if (sortField === "created_at") {
          aValue = a.created_at?._seconds || 0;
          bValue = b.created_at?._seconds || 0;
        } else if (sortField === "price") {
          aValue = (parseFloat(a.pricee) || 0) + (Array.isArray(a.books) ? a.books.reduce((t, b) => t + (parseFloat(b?.amount) || 0), 0) : 0);
          bValue = (parseFloat(b.pricee) || 0) + (Array.isArray(b.books) ? b.books.reduce((t, b) => t + (parseFloat(b?.amount) || 0), 0) : 0);
        }

        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortDirection === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      // Calculate pagination
      const totalItems = filtered.length;
      const totalPages = Math.ceil(totalItems / effectiveItemsPerPage);
      const startIndex = (currentPage - 1) * effectiveItemsPerPage;
      const endIndex = startIndex + effectiveItemsPerPage;
      const paginatedCourses = filtered.slice(startIndex, endIndex);

      return {
        filteredAndSortedCourses: filtered,
        paginatedCourses,
        totalPages,
        totalItems,
      };
    }, [
      courses,
      searchTerm,
      sortField,
      sortDirection,
      statusFilter,
      priceFilter,
      featuredFilter,
      categoryFilter,
      currentPage,
      effectiveItemsPerPage,
    ]);

  // Reset to page 1 when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Effect to reset pagination when filters change
  React.useEffect(() => {
    resetPagination();
  }, [
    searchTerm,
    statusFilter,
    priceFilter,
    featuredFilter,
    categoryFilter,
    viewMode,
  ]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatCourseDate = (timestamp) => {
    return formatDate(timestamp, "N/A");
  };

  const getEffectivePrice = (course) => {
    const basePrice = parseFloat(course.pricee) || 0;
    const booksTotal = Array.isArray(course.books)
      ? course.books.reduce(
          (total, book) => total + (parseFloat(book?.amount) || 0),
          0
        )
      : 0;
    return basePrice + booksTotal;
  };

  const formatPrice = (price, priceStatus) => {
    const numericPrice = parseFloat(price) || 0;
    const isFree = numericPrice <= 0 || priceStatus === "free";
    if (isFree) {
      return (
        <span className="inline-flex items-center rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20 shadow-sm transition-all duration-200 hover:scale-105">
          <SparklesIcon className="h-3 w-3 mr-1 text-emerald-500" />
          Free
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-1.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-600/20 shadow-sm transition-all duration-200 hover:scale-105">
        <CurrencyDollarIcon className="h-3 w-3 mr-1 text-blue-500" />₵
        {numericPrice}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      live: {
        color:
          "text-emerald-700 bg-gradient-to-r from-emerald-50 to-green-50 ring-emerald-600/20 shadow-sm",
        icon: CheckCircleIcon,
        iconColor: "text-emerald-500",
      },
      draft: {
        color:
          "text-amber-700 bg-gradient-to-r from-amber-50 to-yellow-50 ring-amber-600/20 shadow-sm",
        icon: ClockIcon,
        iconColor: "text-amber-500",
      },
      archived: {
        color:
          "text-slate-700 bg-gradient-to-r from-slate-50 to-gray-50 ring-slate-600/20 shadow-sm",
        icon: ExclamationTriangleIcon,
        iconColor: "text-slate-500",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset ${config.color} transition-all duration-200 hover:scale-105`}
      >
        <Icon className={`h-3.5 w-3.5 mr-1.5 ${config.iconColor}`} />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const renderStarRating = (rating) => {
    const stars = [];
    const numRating = parseFloat(rating) || 0;

    for (let i = 1; i <= 5; i++) {
      stars.push(
        <StarIcon
          key={i}
          className={`h-4 w-4 ${
            i <= numRating ? "text-yellow-400 fill-current" : "text-gray-300"
          } transition-all duration-200`}
        />
      );
    }

    return (
      <div className="flex items-center space-x-1">
        <div className="flex space-x-0.5">{stars}</div>
        <span className="text-sm text-gray-600 ml-2 font-medium">
          ({numRating.toFixed(1)})
        </span>
      </div>
    );
  };

  // Beautiful Course Card Component
  const CourseCard = ({ course }) => {
    const enrolledCount =
      course.enrolled_students?.length || course.students || 0;
    const lessonsCount = course.lessons_count || 0;
    const isPopular = enrolledCount > 50; // Mark as popular if more than 50 students
    const isNew =
      course.created_at &&
      new Date(course.created_at._seconds * 1000) >
        new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

    // Check if current user owns this course
    const isOwner =
      currentUser &&
      (course.userId === currentUser.uid ||
        course.author === currentUser.uid ||
        course.author === currentUser.email ||
        course.author === currentUser.displayName);

    return (
      <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-hidden">
        {/* Popular/New Badge */}
        {(isPopular || isNew) && (
          <div className="absolute top-4 left-4 z-10">
            {isPopular && (
              <span className="inline-flex items-center rounded-full bg-gradient-to-r from-orange-400 to-red-400 px-3 py-1 text-xs font-bold text-white shadow-lg">
                <FireIcon className="h-3 w-3 mr-1" />
                Popular
              </span>
            )}
            {isNew && !isPopular && (
              <span className="inline-flex items-center rounded-full bg-gradient-to-r from-purple-400 to-pink-400 px-3 py-1 text-xs font-bold text-white shadow-lg">
                <SparklesIcon className="h-3 w-3 mr-1" />
                New
              </span>
            )}
          </div>
        )}

        {/* Ownership Badge */}
        {isOwner && (
          <div className="absolute top-4 left-4 z-10">
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-400 to-indigo-400 px-3 py-1 text-xs font-bold text-white shadow-lg">
              <AcademicCapIcon className="h-3 w-3 mr-1" />
              Your Course
            </span>
          </div>
        )}

        {/* Course Image */}
        <div className="relative h-48 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 overflow-hidden">
          {course.image_url ? (
            <img
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              src={course.image_url}
              alt={course.name}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <AcademicCapIcon className="h-20 w-20 text-white/80" />
            </div>
          )}
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

          {/* Action Buttons Overlay */}
          <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button
              onClick={() => onView(course)}
              className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors duration-200"
              title="View Course"
            >
              <EyeIcon className="h-4 w-4 text-gray-700" />
            </button>
            {canEditCourse(course) && (
              <button
                onClick={() => onEdit(course)}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors duration-200"
                title="Edit Course"
              >
                <PencilIcon className="h-4 w-4 text-gray-700" />
              </button>
            )}
          </div>
        </div>

        {/* Card Content */}
        <div className="p-6">
          {/* Course Title & Status */}
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200">
              {course.name || "Untitled Course"}
            </h3>
            <div className="flex items-center gap-2">
              {getStatusBadge(course.status)}
              {course.featured && (
                <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-gradient-to-r from-yellow-400 to-orange-500 text-white ring-1 ring-yellow-300 shadow-sm">
                  <svg
                    className="h-3 w-3 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Featured
                </span>
              )}
            </div>
          </div>

          {/* Course Description */}
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {(() => {
              const summary = course.meta?.summary;
              const description = course.meta?.description?.replace(
                /<[^>]*>/g,
                ""
              );
              return summary || description || "No description available";
            })()}
          </p>

          {/* Category */}
          {course.cat_id && (
            <div className="mb-3">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {getCourseCategoryDisplayName(course.cat_id)}
              </span>
            </div>
          )}

          {/* Classroom Indicator */}
          <CourseClassroomIndicator courseId={course.id} />

          {/* Course Stats */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <UsersIcon className="h-4 w-4 mr-1 text-blue-500" />
                <span className="font-medium">{enrolledCount}</span>
              </div>
              <div className="flex items-center">
                <BookOpenIcon className="h-4 w-4 mr-1 text-green-500" />
                <span className="font-medium">{lessonsCount}</span>
              </div>
              {course.meta?.duration && (
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1 text-purple-500" />
                  <span className="font-medium">{course.meta.duration}m</span>
                </div>
              )}
            </div>
            {formatPrice(getEffectivePrice(course), course.price_status)}
          </div>

          {/* Rating */}
          <div className="mb-3">{renderStarRating(course.rating)}</div>

          {/* Author Info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                <UserIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {getAuthorNameFromCourse(course) || "Unassigned"}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center space-x-1">
              {canAssignAuthor(course) && (
                <button
                  onClick={() => onAssignAuthor(course)}
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                  title="Assign Author"
                >
                  <UserPlusIcon className="h-4 w-4" />
                </button>
              )}
              {canAssignStudents(course) && (
                <button
                  onClick={() => onAssignStudents(course)}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                  title="Assign Students"
                >
                  <UsersIcon className="h-4 w-4" />
                </button>
              )}
              {onToggleFeatured && (
                <button
                  onClick={() => onToggleFeatured(course.id, !course.featured)}
                  className={`p-2 rounded-lg transition-colors duration-200 ${
                    course.featured
                      ? "text-yellow-600 hover:bg-yellow-50"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  title={
                    course.featured
                      ? "Remove from featured"
                      : "Mark as featured"
                  }
                >
                  <svg
                    className="h-4 w-4"
                    fill={course.featured ? "currentColor" : "none"}
                    stroke="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              )}
              {canDeleteCourse(course) && (
                <button
                  onClick={() => onDelete(course.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  title="Delete Course"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Hover Effect Border */}
        <div className="absolute inset-0 rounded-2xl ring-2 ring-transparent group-hover:ring-blue-200 transition-all duration-300 pointer-events-none"></div>
      </div>
    );
  };

  // Beautiful Pagination Component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 7;

      if (totalPages <= maxVisiblePages) {
        // Show all pages if total pages is small
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Smart pagination with ellipsis
        if (currentPage <= 4) {
          // Show first pages
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push("ellipsis");
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 3) {
          // Show last pages
          pages.push(1);
          pages.push("ellipsis");
          for (let i = totalPages - 4; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          // Show middle pages
          pages.push(1);
          pages.push("ellipsis");
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push("ellipsis");
          pages.push(totalPages);
        }
      }

      return pages;
    };

    const startItem = (currentPage - 1) * effectiveItemsPerPage + 1;
    const endItem = Math.min(currentPage * effectiveItemsPerPage, totalItems);

    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 px-6 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          {/* Results Info */}
          <div className="flex items-center space-x-4">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{startItem}</span>{" "}
              to <span className="font-semibold text-gray-900">{endItem}</span>{" "}
              of <span className="font-semibold text-gray-900">{totalItems}</span>{" "}
              courses
            </p>

            {/* Items per page selector */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value={8}>8</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
              </select>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg transition-all duration-200 ${
                currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
              }`}
              title="Previous page"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {getPageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                  {page === "ellipsis" ? (
                    <div className="px-3 py-2 text-gray-400">
                      <EllipsisHorizontalIcon className="h-5 w-5" />
                    </div>
                  ) : (
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        currentPage === page
                          ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105"
                          : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                      }`}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg transition-all duration-200 ${
                currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
              }`}
              title="Next page"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Header Skeleton */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          <div className="animate-pulse">
            <div className="h-20 bg-gradient-to-r from-gray-200 to-gray-300"></div>
          </div>
        </div>

        {/* Content Skeleton */}
        {viewMode === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse"
              >
                <div className="h-48 bg-gradient-to-r from-gray-200 to-gray-300"></div>
                <div className="p-6 space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  <div className="flex justify-between items-center">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
            <div className="animate-pulse p-6">
              <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Beautiful Header */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
        <div className="bg-white px-6 py-5 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Title Section */}
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <AcademicCapIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Course Management
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {totalItems} courses •{" "}
                  {
                    filteredAndSortedCourses.filter((c) => c.status === "live")
                      .length
                  }{" "}
                  live •{" "}
                  {
                    filteredAndSortedCourses.filter((c) => c.status === "draft")
                      .length
                  }{" "}
                  draft •{" "}
                  {
                    filteredAndSortedCourses.filter((c) => c.featured === true)
                      .length
                  }{" "}
                  featured
                </p>
              </div>
            </div>

            {/* Controls Section */}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
              {/* Search */}
              <div className="relative flex-1 lg:flex-none">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full lg:w-72 pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200"
                />
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-w-[120px]"
                >
                  <option value="all">All Status</option>
                  <option value="live">Live</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>

                {/* Price Filter */}
                <select
                  value={priceFilter}
                  onChange={(e) => setPriceFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-w-[120px]"
                >
                  <option value="all">All Pricing</option>
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                  <option value="premium">Premium</option>
                </select>

                {/* Featured Filter */}
                <select
                  value={featuredFilter}
                  onChange={(e) => setFeaturedFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-w-[120px]"
                >
                  <option value="all">All Courses</option>
                  <option value="featured">Featured</option>
                  <option value="not-featured">Not Featured</option>
                </select>

                {/* Category Filter */}
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 min-w-[120px]"
                >
                  <option value="all">All Categories</option>
                  {Array.from(
                    new Set(
                      courses.map((course) => course.cat_id).filter(Boolean)
                    )
                  ).map((catId) => (
                    <option key={catId} value={catId}>
                      {catId}
                    </option>
                  ))}
                </select>

                {/* View Toggle */}
                <div className="flex bg-gray-100 rounded-md p-1">
                  <button
                    onClick={() => setViewMode("cards")}
                    className={`p-2 rounded-md transition-all duration-200 ${
                      viewMode === "cards"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                    title="Card View"
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("table")}
                    className={`p-2 rounded-md transition-all duration-200 ${
                      viewMode === "table"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                    title="Table View"
                  >
                    <ListBulletIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {totalItems === 0 ? (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="h-24 w-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <AcademicCapIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No courses found
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ||
              statusFilter !== "all" ||
              priceFilter !== "all" ||
              featuredFilter !== "all" ||
              categoryFilter !== "all"
                ? "Try adjusting your search or filters to find what you're looking for."
                : "Get started by creating your first course to begin building your educational content."}
            </p>
            {/* Add context-specific message for authors */}
            {!searchTerm &&
              statusFilter === "all" &&
              priceFilter === "all" &&
              featuredFilter === "all" &&
              categoryFilter === "all" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">
                        Ready to create your first course?
                      </p>
                      <p className="text-blue-700 mt-1">
                        As an author, you can create engaging courses and share
                        your knowledge with students.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            {(searchTerm ||
              statusFilter !== "all" ||
              priceFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setPriceFilter("all");
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      ) : viewMode === "cards" ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedCourses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      ) : (
        /* Enhanced Table View */
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th scope="col" className="relative px-6 py-4">
                    <span className="sr-only">Actions</span>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    Course
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200/50 transition-colors duration-200"
                    onClick={() => handleSort("author_name")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Author</span>
                      <ChevronUpDownIcon className="h-4 w-4" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200/50 transition-colors duration-200"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      <ChevronUpDownIcon className="h-4 w-4" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    Featured
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    Category
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    Classrooms
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200/50 transition-colors duration-200"
                    onClick={() => handleSort("price")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Price</span>
                      <ChevronUpDownIcon className="h-4 w-4" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    Stats
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    Rating
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200/50 transition-colors duration-200"
                    onClick={() => handleSort("created_at")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      <ChevronUpDownIcon className="h-4 w-4" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedCourses.map((course, index) => (
                  <tr
                    key={course.id}
                    className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50/30"
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => onView(course)}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-100 transition-all duration-200 transform hover:scale-110"
                          title="View Course"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onEdit(course)}
                          className="text-indigo-600 hover:text-indigo-800 p-2 rounded-lg hover:bg-indigo-100 transition-all duration-200 transform hover:scale-110"
                          title="Edit Course"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onAssignAuthor(course)}
                          className="text-purple-600 hover:text-purple-800 p-2 rounded-lg hover:bg-purple-100 transition-all duration-200 transform hover:scale-110"
                          title="Assign Author"
                        >
                          <UserPlusIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onAssignStudents(course)}
                          className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-100 transition-all duration-200 transform hover:scale-110"
                          title="Assign Students"
                        >
                          <UsersIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDelete(course.id)}
                          className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-100 transition-all duration-200 transform hover:scale-110"
                          title="Delete Course"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 h-12 w-12">
                          {course.image_url ? (
                            <img
                              className="h-12 w-12 rounded-xl object-cover shadow-sm"
                              src={course.image_url}
                              alt={course.name}
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-xl bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center shadow-sm">
                              <AcademicCapIcon className="h-6 w-6 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {course.name || "Untitled Course"}
                          </div>
                          <div className="text-xs text-gray-600 truncate">
                            {(() => {
                              const summary = course.meta?.summary;
                              const description =
                                course.meta?.description?.replace(
                                  /<[^>]*>/g,
                                  ""
                                );
                              const text =
                                summary ||
                                description ||
                                "No description available";
                              return text.length > 60
                                ? text.substring(0, 60) + "..."
                                : text;
                            })()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                          <UserIcon className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 truncate max-w-32">
                            {getAuthorNameFromCourse(course) || "Unassigned"}
                          </div>
                          <div className="text-xs text-gray-500">Instructor</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(course.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {course.featured ? (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-gradient-to-r from-yellow-400 to-orange-500 text-white ring-1 ring-yellow-300 shadow-sm">
                          <svg
                            className="h-3 w-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Featured
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {course.cat_id ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getCourseCategoryDisplayName(course.cat_id)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <CourseClassroomIndicator courseId={course.id} />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatPrice(getEffectivePrice(course), course.price_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="space-y-1">
                        <div className="flex items-center text-xs">
                          <UsersIcon className="h-3 w-3 mr-2 text-blue-500" />
                          <span className="font-medium">
                            {course.enrolled_students?.length ||
                              course.students ||
                              0}{" "}
                            students
                          </span>
                        </div>
                        <div className="flex items-center text-xs">
                          <BookOpenIcon className="h-3 w-3 mr-2 text-green-500" />
                          <span className="font-medium">
                            {course.lessons_count || 0} lessons
                          </span>
                        </div>
                        {course.meta?.duration && (
                          <div className="flex items-center text-xs">
                            <ClockIcon className="h-3 w-3 mr-2 text-purple-500" />
                            <span className="font-medium">
                              {course.meta.duration}m
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderStarRating(course.rating)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="font-medium">
                        {formatCourseDate(course.created_at)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination />
    </div>
  );
};

export default CourseTable;
